import {
  Exploration,
  Field,
  Metric,
  Model,
  PipelineStateRelation,
  RecordsLikeCell,
  Relation,
} from '@/explore/types';
import { getModelOrThrow } from '@/explore/model/utils';
import { isRecordsLikeCell } from '@/core/cell';
import { getBaseStateOrThrow } from '@/explore/pipeline/state';

import {
  constructRelationOptionValue,
  findRelationsForCell,
  getRelationTypeLabel,
  pipelineContainsReference,
} from './utils';
import { RelationDescription } from './relation';

import styles from './select-options.module.scss';

const createRelatedCellOption = (
  relation: PipelineStateRelation,
  relationsForCell: Relation[],
  cell: RecordsLikeCell,
  currentCell: RecordsLikeCell,
  fields: Field[],
  models: Model[],
  exploration: Exploration,
) => {
  const isCircularReference = pipelineContainsReference(
    currentCell.pipeline.pipelineId,
    cell.pipeline,
    exploration,
  );

  return {
    value: constructRelationOptionValue({
      key: relation.key,
      baseModelId: relation.baseModelId,
      pipelineId: cell.pipeline.pipelineId,
    }),
    label:
      relationsForCell.length === 1
        ? (cell.title ?? '(Untitled)')
        : `${cell.title ?? '(Untitled)'} as ${relation.name}`,
    disabled: isCircularReference,
    title: isCircularReference
      ? 'Adding data from this table would create a circular reference'
      : '',
    tooltip: (
      <RelatedCellOptionTooltip
        relation={relation}
        relations={relationsForCell}
        fields={fields}
        models={models}
        cell={cell}
      />
    ),
  };
};

export const createRelatedCellOptions = (
  relations: PipelineStateRelation[],
  fields: Field[],
  models: Model[],
  metrics: Metric[],
  exploration: Exploration,
  currentCell: RecordsLikeCell,
) =>
  exploration.view.cells
    .filter(({ id }) => id !== currentCell.id)
    .filter(isRecordsLikeCell)
    .reduce(
      (acc, cell) => {
        const relationsForCell = findRelationsForCell(
          relations,
          models,
          metrics,
          cell,
          exploration,
        );

        return acc.concat(
          relationsForCell.map((relation) =>
            createRelatedCellOption(
              relation,
              relationsForCell,
              cell,
              currentCell,
              fields,
              models,
              exploration,
            ),
          ),
        );
      },
      [] as { value: string; label: string }[],
    );

const createJoinableCellOption = (
  cell: RecordsLikeCell,
  currentCell: RecordsLikeCell,
  exploration: Exploration,
) => {
  const isCircularReference = pipelineContainsReference(
    currentCell.pipeline.pipelineId,
    cell.pipeline,
    exploration,
  );

  return {
    value: constructRelationOptionValue({ pipelineId: cell.pipeline.pipelineId }),
    label: cell.title ?? '(Untitled)',
    disabled: isCircularReference,
    title: isCircularReference
      ? 'Adding data from this table would create a circular reference'
      : '',
  };
};

export const createJoinableCellOptions = (exploration: Exploration, currentCell: RecordsLikeCell) =>
  exploration.view.cells.reduce<{ value: string; label: string }[]>((acc, cell) => {
    if (isRecordsLikeCell(cell) && cell.id !== currentCell.id) {
      acc.push(createJoinableCellOption(cell, currentCell, exploration));
      return acc;
    }
    return acc;
  }, []);

interface RelatedCellOptionTooltipProps {
  relation: PipelineStateRelation;
  relations: Relation[];
  fields: Field[];
  models: Model[];
  cell: RecordsLikeCell;
}

export const RelatedCellOptionTooltip = (props: RelatedCellOptionTooltipProps) => {
  const { relation, relations, fields, models, cell } = props;
  const model = getModelOrThrow(models, relation.modelId);
  const { fields: relatedFields } = getBaseStateOrThrow(relation.modelId, models);
  return (
    <aside className={styles.optionTooltip}>
      <strong className={styles.header}>
        {cell.title ?? '(Untitled Block)'}{' '}
        <span className={styles.type}>{getRelationTypeLabel(relation.type)}</span>
      </strong>
      <p>
        <strong>Relation:</strong> {relation.name}
        <br />
        <strong>Model:</strong> {model.name}
      </p>
      <RelationDescription
        relation={relation}
        relations={relations}
        fields={fields}
        models={models}
        relatedEntityName={cell.title ?? '(Untitled Block)'}
        relatedFields={relatedFields}
        relatedEntityType="cell"
      />
    </aside>
  );
};
